'use client';

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from '@tanstack/react-router';
import { getImageUrl, IMAGE_SIZES } from '../lib/tmdb';

interface CategoryCardProps {
  id: string;
  name: string;
  displayName: string;
  type: 'language' | 'genre';
  imageUrl?: string | null;
  index: number;
  isFocused?: boolean;
  isTV?: boolean;
  onFocus?: () => void;
}

const CategoryCard = React.forwardRef<HTMLDivElement, CategoryCardProps>(
  ({ id, name, displayName, type, imageUrl, index, isFocused, isTV, onFocus }, ref) => {
    const navigate = useNavigate();
    const [imageError, setImageError] = useState(false);

    // Use poster or backdrop, fallback to placeholder
    const imageSrc = imageUrl 
      ? getImageUrl(imageUrl, IMAGE_SIZES.poster.medium)
      : '/placeholder-movie.png';

    const handleClick = () => {
      navigate({
        to: '/movies/browse/$type/$filter',
        params: { type, filter: id }
      });
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    };

    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3, delay: index * 0.05 }}
        whileHover={{ scale: 1.05, y: -8 }}
        whileTap={{ scale: 0.98 }}
        className={`
          relative group cursor-pointer
          aspect-[3/4] w-full
          rounded-xl overflow-hidden
          bg-gray-800
          ${isFocused && isTV ? 'ring-4 ring-white ring-offset-2 ring-offset-black' : ''}
        `}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onFocus={onFocus}
        tabIndex={isTV ? 0 : -1}
        role="button"
        aria-label={`Browse ${displayName}`}
      >
        {/* Image Background */}
        <img
          src={imageError ? '/placeholder-movie.png' : imageSrc}
          alt={displayName}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
          onError={() => {
            if (!imageError) {
              setImageError(true);
            }
          }}
        />

        {/* Gradient Overlay for title readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent opacity-80" />

        {/* Title at Bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4">
          <h3 className="text-white font-bold text-sm sm:text-base md:text-lg line-clamp-2 drop-shadow-lg">
            {name}
          </h3>
        </div>

        {/* Overlay on hover */}
        <motion.div
          initial={{ opacity: 0 }}
          className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        />
      </motion.div>
    );
  }
);

CategoryCard.displayName = 'CategoryCard';

export default CategoryCard;

